import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import DiamondDetails from "./pages/DiamondDetails/DiamondDetails";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import PasswordModal from "./components/PasswordModal/PasswordModal";
import LinkExpired from "./components/LinkExpired/LinkExpired";
import "./ShareFeature.css";
import debounce from "lodash.debounce";
import { useMemo } from "react";
import { DiamondSkeleton } from "./pages/DiamondDetails/DiamondSkeleton";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import ShareNotFound from "./components/ShareNotFound/ShareNotFound";

function FullPageLoader({primaryColor}) {
    return (
        <div className="loader-overlay">
            <div className="loader-box">
                <div className="loader-spinner" style={{borderTopColor:primaryColor}}></div>
                <div className="loader-text" style={{color:primaryColor}}>Loading ...</div>
            </div>
        </div>
    );
}

export default function ShareFeature() {
    const { refId } = useParams();
    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const urlTitle = params.get("title");
    const urlShape = params.get("shape");
    const [pageState, setPageState] = useState("loading");
    const [customerToken, setCustomerToken] = useState(null);
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [loading, setLoading] = useState(true);
    const [passwordRequired, setPasswordRequired] = useState(false);
    const [data, setData] = useState(null);

    const calledRef = useRef(false);

       const fallbackTitle = "Diamond Details";
    const fallbackDescription = "View diamond details";

    const fallbackImage =
        "https://assets.diamondsoncall.com/assets/images/email/DiamondShare.svg";

    const title =
        urlTitle ||
        (data
            ? `${data?.showsharecertificate ? data?.lab : ""} ${data?.shape || ""} ${
                  data?.carat ? `${data?.carat.toFixed(2)}ct` : ""
              } ${data?.color || ""} ${data?.clarity || ""} ${data?.cut || ""} ${
                  data?.polish || ""
              } ${data?.symmetry || ""} ${data?.fluorescence || ""}`.trim()
            : fallbackTitle);

    const description = title || fallbackDescription;

    const image =
        data?.image ||
        (urlShape
            ? `https://assets.diamondsoncall.com/assets/shapes/svg_shape/${urlShape.toLowerCase()}.svg`
            : fallbackImage);

    const url = window.location.href;

    // ⭐ cookie logic
    useEffect(() => {
        let token = Cookies.get("customer_token");

        if (!token) {
            token = uuidv4();
            Cookies.set("customer_token", token, { expires: 365 });
        }

        setCustomerToken(token);
    }, []);

    const fetchData = async () => {
        if (!customerToken) return;

        setLoading(true);
        setPasswordError("");

        try {
            const payload = {
                customer_token: customerToken,
                share_token: refId,
                password,
            };

            const res = await axios.post(
                `${process.env.REACT_APP_NODE_API_BETA_URL}/admin/get-share-details`,
                payload,
            );

            if (!res.data.success) {
                if (!res.data.success) {
                    switch (res.data.message) {
                        case "This share link has expired":
                            setPageState("expired");
                            return;

                        case "Share Details Not Found!":
                            setPageState("not-found");
                            return;

                        case "Password is Required!":
                            setPasswordRequired(true);
                            setPageState("password");
                            return;

                        case "Invalid Password!":
                            setPasswordRequired(true);
                            setPasswordError("Invalid password");
                            setPageState("password");
                            return;

                        default:
                            setPageState("not-found");
                            return;
                    }
                }

                setPasswordRequired(false);
                setPasswordError("");
                setData(res.data.data);
                setPageState("data");
                // // ⭐ PRIORITY 1
                // if (res.data.message === "This share link has expired") {
                //     setExpired(true);
                //     return;
                // }

                // // ⭐ PRIORITY 2
                // if (res.data.message === "Password is Required!") {
                //     setPasswordRequired(true);
                //     return;
                // }

                // if (res.data.message === "Invalid Password!") {
                //     setPasswordRequired(true);
                //     setPasswordError("Invalid password");
                //     return;
                // }
            }

            setPasswordRequired(false);
            setPasswordError("");
            setData(res.data?.data);
        } catch (err) {
            console.log(err);
            // setExpired(true);
            setPageState("not-found");
        } finally {
            setLoading(false);
        }
    };
    const primaryColor =
        data?.diamondType === "N"
            ? data?.naturalPrimaryColor || "#EFB33D"
            : data?.diamondType === "L"
              ? data?.labPrimaryColor || "#EFB33D"
              : "#EFB33D";
    const primaryFontColor =
        data?.diamondType === "N"
            ? data?.naturalFontColor || "#2E384D"
            : data?.diamondType === "L"
              ? data?.labFontColor || "#2E384D"
              : "#2E384D";

    useEffect(() => {
        if (!customerToken || calledRef.current) return;
        calledRef.current = true;
        fetchData();
    }, [customerToken]);

    const debouncedFetch = useMemo(
        () => debounce(fetchData, 400),
        [customerToken, password, refId],
    );
 
    const handlePasswordSubmit = () => {
        if (!password) {
            setPasswordError("Please enter password");
            return;
        }
        debouncedFetch();
    };

    if (pageState === "expired") {
        return <LinkExpired />;
    }

    if (pageState === "not-found") {
        return <ShareNotFound />;
    }
    if (loading && !passwordRequired) return <DiamondSkeleton />;
    if (loading) return <FullPageLoader primaryColor={primaryColor} />;

    if (data)
        return (
            <>
                {/* {data && ( */}
                <Helmet>
                    <title>{title}</title>

                    <meta name="description" content={description} />

                    <meta property="og:type" content="website" />
                    <meta property="og:title" content={title} />
                    <meta property="og:description" content={description} />
                    <meta property="og:image" content={image} />
                    <meta property="og:url" content={url} />

                    <link rel="canonical" href={url} />

                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={title} />
                    <meta name="twitter:description" content={description} />
                    <meta name="twitter:image" content={image} />
                </Helmet>
                {/* )} */}
                <div className={passwordRequired ? "blurred" : ""}>
                    <DiamondDetails data={data} primaryColor={primaryColor} primaryFontColor={primaryFontColor} />
                </div>
                <PasswordModal
                    open={passwordRequired}
                    password={password}
                    setPassword={setPassword}
                    onSubmit={handlePasswordSubmit}
                    error={passwordError}
                    loading={loading}
                    setPasswordError={setPasswordError}
                    primaryColor={primaryColor}
                    primaryFontColor={data?.naturalFontColor ? primaryFontColor : "#fff"}
                />
            </>
        );

    return (
        <>
            {/* {data && ( */}
            <Helmet>
                <title>{title}</title>

                <meta name="description" content={description} />

                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={image} />
                <meta property="og:url" content={url} />

                <link rel="canonical" href={url} />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={image} />
            </Helmet>
            {/* )} */}
            <PasswordModal
                open={passwordRequired}
                password={password}
                setPassword={setPassword}
                onSubmit={handlePasswordSubmit}
                error={passwordError}
                loading={loading}
                setPasswordError={setPasswordError}
                primaryColor={primaryColor}
                primaryFontColor={data?.naturalFontColor ? primaryFontColor : "#fff"}
            />
        </>
    );
}
